import { useId, useMemo, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import { GoogleIcon } from "../assets/icons";
import { prepareOAuthHandoff } from "../utils/oauth_handoff";
import { getOAuthGmailUrl } from "../../pages/auth/auth_components/oauth_google_button";

const INPUT_CLASS = [
	"w-full h-12 px-4 rounded-md border bg-auth-bg text-auth-text text-base outline-none",
	"placeholder:text-auth-sub focus:border-primary transition-colors",
].join(" ");

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
	/** i18n key of the error shown under the field. */
	error?: string;
	/** i18n key of a confirmation (e.g. "passwords match"): green frame, a tick and a hidden label. */
	confirmed?: string;
}

const fieldBorder = (error?: string, confirmed?: string) => {
	if (error) return "border-error";
	return confirmed ? "border-teal focus:border-teal!" : "border-auth-line";
};

const EYE_CLASS = [
	"absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center",
	"rounded-full border-0 bg-transparent cursor-pointer text-auth-sub hover:text-primary",
].join(" ");

// Room on the right for the eye, the tick, or both.
const rightPadding = (isPassword: boolean, isConfirmed: boolean) => {
	if (isPassword && isConfirmed) return "pr-[72px]";
	if (isPassword) return "pr-11";
	return isConfirmed ? "pr-10" : "";
};

/** `required` marks the field with an asterisk, as the /auth page does; the forms validate themselves. */
export const AuthField = ({
	error,
	confirmed,
	className = "",
	required,
	type,
	...props
}: FieldProps) => {
	const { t } = useTranslation();
	const [shown, setShown] = useState(false);
	const errorId = useId();
	const isPassword = type === "password";
	const isConfirmed = !!confirmed && !error;
	// E2E finds the asterisk and the error by the input's own test id.
	const testId = (props as { "data-testid"?: string })["data-testid"];
	return (
		<label className="flex flex-col gap-1">
			<span className="relative">
				<input
					{...props}
					type={isPassword && shown ? "text" : type}
					required={required}
					aria-invalid={!!error}
					aria-describedby={error ? errorId : undefined}
					className={`${INPUT_CLASS} ${fieldBorder(error, confirmed)} ${rightPadding(isPassword, isConfirmed)} ${className}`}
				/>
				{isPassword && (
					<button
						type="button"
						aria-label={t(
							shown ? "auth-page.buttons.hide-password" : "auth-page.buttons.show-password",
						)}
						className={EYE_CLASS}
						onClick={() => setShown(s => !s)}
					>
						{shown ? <EyeSlashIcon size={18} /> : <EyeIcon size={18} />}
					</button>
				)}
				{isConfirmed && (
					<span
						className={`absolute top-1/2 -translate-y-1/2 flex text-teal ${isPassword ? "right-11" : "right-3"}`}
					>
						<CheckIcon size={18} weight="bold" aria-hidden="true" />
						<span className="sr-only">{t(confirmed)}</span>
					</span>
				)}
				{required && (
					<span
						aria-hidden="true"
						data-testid={testId && `${testId}-required`}
						className="absolute -top-2 -right-0.5 text-primary font-semibold select-none"
					>
						*
					</span>
				)}
			</span>
			{error && (
				<span
					id={errorId}
					data-testid={testId && `${testId}-error`}
					className="text-[13px] leading-[1.35] text-error"
				>
					{t(error)}
				</span>
			)}
		</label>
	);
};

/** The confirmation key for a repeat-password field, once both are typed and equal. */
export const passwordsMatch = (password: string, confirm: string): string | undefined =>
	confirm && confirm === password ? "auth.passwords-match" : undefined;

export const BACK_LINK_CLASS = [
	"self-start inline-flex items-center gap-1 p-0 mb-1 border-0 bg-transparent cursor-pointer",
	"text-sm text-auth-sub hover:text-primary",
].join(" ");

export const PRIMARY_BUTTON_CLASS = [
	"w-full h-12 rounded-md border-0 bg-primary text-white text-base font-semibold cursor-pointer",
	"hover:bg-primary-dark disabled:opacity-60 disabled:cursor-default transition-colors",
].join(" ");

const GOOGLE_CLASS = [
	"w-full h-12 flex items-center justify-center gap-2.5 rounded-md no-underline",
	"border border-auth-line bg-auth-card text-auth-text text-base font-semibold",
	"hover:border-auth-sub transition-colors",
].join(" ");

/** Google leaves the page; the handoff brings the reader back to `returnTo`. */
export const GoogleButton = ({
	children,
	returnTo,
	onLeave,
}: {
	children: ReactNode;
	returnTo: string;
	/** Runs as the reader leaves for Google, next to the handoff. */
	onLeave?: () => void;
}) => {
	const handoff = useMemo(() => prepareOAuthHandoff(returnTo), [returnTo]);
	const leave = () => {
		handoff.remember();
		onLeave?.();
	};
	return (
		<a
			href={getOAuthGmailUrl(handoff.state).href}
			onClick={leave}
			onAuxClick={leave}
			onContextMenu={leave}
			data-testid="oauth-google-button"
			className={GOOGLE_CLASS}
		>
			<GoogleIcon /> {children}
		</a>
	);
};

export const OrDivider = () => {
	const { t } = useTranslation();
	return (
		<div className="flex items-center gap-3 text-[13px] text-auth-sub">
			<span className="flex-1 h-px bg-auth-line" />
			{t("auth.or")}
			<span className="flex-1 h-px bg-auth-line" />
		</div>
	);
};

export const FormError = ({ error }: { error: string }) => {
	const { t } = useTranslation();
	if (!error) return null;
	return (
		<p role="alert" className="m-0 text-[13px] leading-[1.35] text-error">
			{t(error)}
		</p>
	);
};
